import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, CreditCard, Clock } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';

const RenewalModal = () => {
  const navigate = useNavigate();
  const { showRenewalModal, dismissRenewalModal, expiryDate, isCancelled } = useSubscription();

  const handleRenew = () => {
    dismissRenewalModal();
    navigate('/choose-plan');
  };

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={showRenewalModal} onOpenChange={dismissRenewalModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            Subscription Expired
          </DialogTitle>
          <DialogDescription className="text-center">
            {isCancelled 
              ? 'Your cancelled subscription has ended.'
              : 'Your subscription has expired.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Expired on: {formatExpiryDate(expiryDate)}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  You can view your data but cannot create, edit, or delete entries.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Renew now</strong> to continue managing your venue bookings, leads, and more.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button 
            onClick={handleRenew}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Renew Subscription
          </Button>
          <Button 
            variant="outline" 
            onClick={dismissRenewalModal}
            className="w-full text-gray-600"
          >
            Remind me later
          </Button>
          <p className="text-xs text-center text-gray-500 mt-2">
            This reminder will appear every 30 seconds
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RenewalModal;
